import { z } from 'zod'

import {
  DOC_CHAT_IMAGE_MIME_TYPES,
  type DocChatPendingAttachment,
} from './docChatImageAttachments'

const DB_NAME = 'docChatAttachments'
const STORE_NAME = 'attachments'
const VERSION = 1

/** Attachments outlive a chat tab but not a working session. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
/** Newest-first cap, so a long session cannot fill the origin's quota. */
const MAX_ENTRIES = 40

const StoredAttachmentSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  mimeType: z.enum(DOC_CHAT_IMAGE_MIME_TYPES),
  dataBase64: z.string().min(1),
  timestamp: z.number(),
})

type StoredAttachment = z.infer<typeof StoredAttachmentSchema>

/**
 * Chat image attachments, keyed by attachment id.
 *
 * Search state is persisted to session storage and must not carry megabytes of
 * base64, and React state dies with the chat tab — switching tabs and back was
 * losing the image. IndexedDB is the one store with room for the bytes and a
 * lifetime longer than the mounted tab.
 *
 * Every operation resolves rather than rejects: with no IndexedDB (private
 * browsing, blocked storage) the transcript falls back to a filename chip,
 * which is a degraded thumbnail rather than a broken chat.
 */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null)
      return
    }
    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(DB_NAME, VERSION)
    } catch {
      resolve(null)
      return
    }
    request.onerror = () => resolve(null)
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
        store.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }
  })
}

function toAttachment(stored: StoredAttachment): DocChatPendingAttachment {
  return {
    id: stored.id,
    name: stored.name,
    mimeType: stored.mimeType,
    dataBase64: stored.dataBase64,
    previewUrl: `data:${stored.mimeType};base64,${stored.dataBase64}`,
  }
}

export async function saveAttachments(
  attachments: readonly DocChatPendingAttachment[]
): Promise<void> {
  if (!attachments.length) return
  const db = await openDb()
  if (!db) return
  await new Promise<void>((resolve) => {
    let transaction: IDBTransaction
    try {
      transaction = db.transaction(STORE_NAME, 'readwrite')
    } catch {
      resolve()
      return
    }
    const store = transaction.objectStore(STORE_NAME)
    const timestamp = Date.now()
    for (const attachment of attachments) {
      store.put({
        id: attachment.id,
        name: attachment.name,
        mimeType: attachment.mimeType,
        dataBase64: attachment.dataBase64,
        timestamp,
      } satisfies StoredAttachment)
    }
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => resolve()
    transaction.onabort = () => resolve()
  })
  void pruneAttachments()
}

export async function loadAttachments(
  ids: readonly string[]
): Promise<DocChatPendingAttachment[]> {
  if (!ids.length) return []
  const db = await openDb()
  if (!db) return []
  return new Promise((resolve) => {
    let transaction: IDBTransaction
    try {
      transaction = db.transaction(STORE_NAME, 'readonly')
    } catch {
      resolve([])
      return
    }
    const store = transaction.objectStore(STORE_NAME)
    const found: DocChatPendingAttachment[] = []
    for (const id of ids) {
      const request = store.get(id)
      request.onsuccess = () => {
        const parsed = StoredAttachmentSchema.safeParse(request.result)
        if (parsed.success) found.push(toAttachment(parsed.data))
      }
    }
    transaction.oncomplete = () => resolve(found)
    transaction.onerror = () => resolve(found)
    transaction.onabort = () => resolve(found)
  })
}

export async function clearStoredAttachments(): Promise<void> {
  const db = await openDb()
  if (!db) return
  await new Promise<void>((resolve) => {
    let transaction: IDBTransaction
    try {
      transaction = db.transaction(STORE_NAME, 'readwrite')
    } catch {
      resolve()
      return
    }
    transaction.objectStore(STORE_NAME).clear()
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => resolve()
    transaction.onabort = () => resolve()
  })
}

/** Drop stale entries, then trim to the newest MAX_ENTRIES. */
async function pruneAttachments(): Promise<void> {
  const db = await openDb()
  if (!db) return
  await new Promise<void>((resolve) => {
    let transaction: IDBTransaction
    try {
      transaction = db.transaction(STORE_NAME, 'readwrite')
    } catch {
      resolve()
      return
    }
    const store = transaction.objectStore(STORE_NAME)
    const cutoff = Date.now() - MAX_AGE_MS
    const request = store.index('timestamp').openCursor()
    const live: { id: string; timestamp: number }[] = []
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) {
        const excess = live.length - MAX_ENTRIES
        if (excess > 0) {
          live.sort((a, b) => a.timestamp - b.timestamp)
          for (const entry of live.slice(0, excess)) store.delete(entry.id)
        }
        return
      }
      const parsed = StoredAttachmentSchema.safeParse(cursor.value)
      if (!parsed.success || parsed.data.timestamp < cutoff) {
        cursor.delete()
      } else {
        live.push({ id: parsed.data.id, timestamp: parsed.data.timestamp })
      }
      cursor.continue()
    }
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => resolve()
    transaction.onabort = () => resolve()
  })
}
